import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  forwardRef,
  Input,
  OnDestroy,
  OnInit,
  ViewChild
} from '@angular/core';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR } from '@angular/forms';
import { ConnectedPosition } from '@angular/cdk/overlay';
import { ObjectDefinitionModel } from '@base/modules/rest/bbs-object/model/object-definition.model';
import { CustomFieldModel } from '@base/modules/rest/custom-field/model/custom-field.model';
import { ObjectRestServiceCreator } from '@base/modules/rest/object/object-rest-service-creator';
import { CustomFieldTypesEnum } from '@base/modules/rest/custom-field/enums/custom-field-types.enum';
import { BehaviorSubject, defer, Observable, of, Subject } from 'rxjs';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  finalize,
  map,
  shareReplay,
  switchMap,
  take,
  takeUntil,
  tap
} from 'rxjs/operators';
import {
  FieldPathOption
} from '@base/../views/secured/beyond-core/settings/applications-settings/dialogs/rules-builder-dialog/rules-builder.model';
import { FieldPathResolverService } from './services/field-path-resolver.service';

@Component({
  selector: 'app-field-path-input',
  templateUrl: './field-path-input.component.html',
  styleUrls: ['./field-path-input.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FieldPathInputComponent),
      multi: true,
    },
  ],
})
export class FieldPathInputComponent implements ControlValueAccessor, OnInit, OnDestroy {
  @Input() initialObjectDefinition!: ObjectDefinitionModel;

  @ViewChild('pathInputField') pathInputField!: ElementRef<HTMLInputElement>;
  @ViewChild('triggerOrigin') triggerOrigin!: ElementRef;

  fullPath: string | null = null;
  segments: string[] = [];
  segmentLabels: string[] = [];
  currentInput = new FormControl('');
  suggestions$: Observable<FieldPathOption[]> = of([]);
  canEnterMoreSegments = true;
  isFinalEditable = false;
  showPlaceholder = true;

  // CDK Overlay properties
  isDropdownOpen = false;
  triggerRect: DOMRect | null = null;
  connectedPositions: ConnectedPosition[] = [
    {
      originX: 'start',
      originY: 'bottom',
      overlayX: 'start',
      overlayY: 'top',
    },
    {
      originX: 'start',
      originY: 'top',
      overlayX: 'start',
      overlayY: 'bottom',
    },
  ];

  private isLoadingSuggestionsSubject = new BehaviorSubject<boolean>(false);
  private definitionCache = new Map<string, Observable<ObjectDefinitionModel | null>>();
  private destroy$ = new Subject<void>();

  isLoadingSuggestions$: Observable<boolean> = this.isLoadingSuggestionsSubject.asObservable();

  constructor(private objectRestServiceCreator: ObjectRestServiceCreator,
              private cdRef: ChangeDetectorRef,
              private fieldPathResolverService: FieldPathResolverService) {
  }

  ngOnInit(): void {
    if (!this.initialObjectDefinition) {
      console.error('FieldPathInputComponent requires initialObjectDefinition input!');
      return;
    }

    this.checkPathEndType(this.fullPath);

    this.currentInput.valueChanges
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: () => {
          if (this.canEnterMoreSegments || this.isFinalEditable) {
            this.isLoadingSuggestionsSubject.next(true);
            this.openDropdown();
            this.loadSuggestionsForCurrentPath(true);
          } else {
            this.closeDropdown();
          }
        },
        error: (err) => {
          console.error('[FieldPathInput] Error in input valueChanges subscription:', err);
        },
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.definitionCache.clear();
    this.closeDropdown();
  }

  // CDK Overlay methods
  openDropdown(): void {
    if (!this.isDropdownOpen && this.triggerOrigin) {
      this.triggerRect = this.triggerOrigin.nativeElement.getBoundingClientRect();
      this.isDropdownOpen = true;
      this.cdRef.markForCheck();
      this.cdRef.detectChanges();
    }
  }

  closeDropdown(): void {
    this.isDropdownOpen = false;
    this.cdRef.markForCheck();
  }

  onBackdropClick(): void {
    this.closeDropdown();
  }

  onOverlayOutsideClick(): void {
    this.closeDropdown();
  }

  // --- Path Parsing & Manipulation ---
  private parsePath(): void {
    this.segments = this.fullPath ? this.fullPath.split('.').filter(s => !!s) : [];
    this.segmentLabels = this.resolveSegmentLabels();
  }

  private resolveSegmentLabels(): string[] {
    if (this.segments.length === 0) {
      return [];
    }

    const labels: string[] = [];
    let currentDefinition: ObjectDefinitionModel | null = this.initialObjectDefinition;

    for (let i = 0; i < this.segments.length; i++) {
      const segment = this.segments[i];

      if (!currentDefinition?.fields) {
        // No fields available, fallback to segment name
        labels.push(segment.charAt(0).toUpperCase() + segment.slice(1));
        continue;
      }

      const field = currentDefinition.fields.find(f => f.name === segment);
      if (!field) {
        // Field not found, fallback to segment name
        labels.push(segment.charAt(0).toUpperCase() + segment.slice(1));
        continue;
      }

      // Use the field's translated label
      labels.push(field.label);

      // If this is a lookup field and we need to resolve the next segment,
      // try to get the lookup object's definition from cache
      if (i < this.segments.length - 1 && field.type === CustomFieldTypesEnum.LOOKUP && field.lookupObject?.name) {
        const objectName = field.lookupObject.name;
        const cachedDefinition$ = this.definitionCache.get(objectName);

        if (cachedDefinition$) {
          // Try to get the cached definition using a synchronous approach
          // Since the observable is already cached and likely resolved
          let resolvedDefinition: ObjectDefinitionModel | null = null;
          let hasResolved = false;

          const subscription = cachedDefinition$.subscribe({
            next: (def) => {
              resolvedDefinition = def;
              hasResolved = true;
            },
            error: () => {
              hasResolved = true;
            },
          });

          // Immediately unsubscribe since we just want the current value
          subscription.unsubscribe();

          if (hasResolved && resolvedDefinition) {
            currentDefinition = resolvedDefinition;
            continue; // Continue with the next segment using the lookup definition
          }
        }

        // If we can't resolve the lookup definition synchronously,
        // add the remaining segments with fallback formatting
        for (let j = i + 1; j < this.segments.length; j++) {
          const remainingSegment = this.segments[j];
          labels.push(remainingSegment.charAt(0).toUpperCase() + remainingSegment.slice(1));
        }
        break;
      }
    }

    return labels;
  }

  removeSegment(index: number): void {
    this.fullPath = this.segments.slice(0, index).join('.');
    this.parsePath();
    this.onChange(this.fullPath);
    this.onTouched();
    this.canEnterMoreSegments = true;
    this.showPlaceholder = true;
    this.focusInput();
    this.loadSuggestionsForCurrentPath(true);
    this.cdRef.markForCheck();
  }

  clearPath(): void {
    this.fullPath = null;
    this.parsePath(); // This will empty the segments array
    this.onChange(null); // Notify parent form
    this.onTouched(); // Mark as touched
    this.canEnterMoreSegments = true; // Allow input again
    this.currentInput.setValue(''); // Clear any partial input
    this.focusInput(); // Focus the input field
    this.loadSuggestionsForCurrentPath(false);
    this.showPlaceholder = true;
    this.cdRef.markForCheck(); // Update view
  }

  // --- Input & Suggestion Logic ---
  focusInput(): void {
    if (this.canEnterMoreSegments && !this.currentInput.disabled) {
      setTimeout(() => {
        this.pathInputField?.nativeElement?.focus();
        this.openDropdown();
      }, 0);
    }
  }

  onInputClick(): void {
    this.loadSuggestionsForCurrentPath(true);
  }

  onInputKeyDown(event: KeyboardEvent): void {
    if (!this.canEnterMoreSegments && !this.isFinalEditable) {
      return;
    }

    const inputValue = this.currentInput.value ?? '';
    if (event.key === 'Backspace' && inputValue === '' && this.segments.length > 0) {
      this.removeSegment(this.segments.length - 1);
      event.preventDefault();
    } else if (event.key === 'Enter') {
      event.preventDefault();
    }
  }

  selectSuggestion(suggestion: FieldPathOption): void {
    this.fullPath = suggestion.path;
    this.parsePath();
    this.onChange(this.fullPath);
    this.currentInput.setValue('', {emitEvent: false});
    this.closeDropdown();

    const isLookup = suggestion.field.type === CustomFieldTypesEnum.LOOKUP && suggestion.field.lookupObject?.name;
    const isNestedLookup = this.fullPath.includes('.') && !isLookup;

    // Pre-load lookup definition if this is a lookup field to ensure proper label resolution
    if (isLookup) {
      const objectName = suggestion.field.lookupObject.name;
      this.getCachedDefinition(objectName)
        .pipe(take(1), takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            this.segmentLabels = this.resolveSegmentLabels();
            this.cdRef.markForCheck();

            this.canEnterMoreSegments = true;
            this.isFinalEditable = false;
            this.showPlaceholder = true;

            this.focusInput();
            this.loadSuggestionsForCurrentPath(true);
          },
          error: (err) => {
            console.error(`[FieldPathInput] Error pre-loading definition for ${objectName}:`, err);
          },
        });
    } else {
      if (isNestedLookup) {
        this.canEnterMoreSegments = false;
        this.isFinalEditable = true;
        this.showPlaceholder = false;

        setTimeout(() => {
          this.pathInputField?.nativeElement?.focus();
        });
      } else {
        this.canEnterMoreSegments = false;
        this.isFinalEditable = false;
        this.showPlaceholder = true;
        this.pathInputField?.nativeElement?.blur();
      }
    }
    this.cdRef.markForCheck();
  }

  private fetchSuggestions(currentInputSegment: string): Observable<FieldPathOption[]> {
    const parentPath = this.segments.join('.');
    let currentDefinition$: Observable<ObjectDefinitionModel | null>;

    if (parentPath) {
      const parentField$ = this.fieldPathResolverService.resolveFieldPath(parentPath, this.initialObjectDefinition)
        .pipe(
          catchError(err => {
            console.error(`[FieldPathInput] fetchSuggestions - Error resolving parent path "${parentPath}":`, err);
            return of(null);
          }),
          shareReplay(1)
        );

      currentDefinition$ = parentField$
        .pipe(
          switchMap(parentField => {
            if (parentField?.type === CustomFieldTypesEnum.LOOKUP && parentField.lookupObject?.name) {
              const objectName = parentField.lookupObject.name;
              return this.getCachedDefinition(objectName)
                .pipe(
                  catchError(err => {
                    console.error(`[FieldPathInput] fetchSuggestions - Error getting definition for "${objectName}":`, err);
                    return of(null);
                  })
                );
            } else {
              console.warn('[FieldPathInput] Parent field is not a lookup or missing lookupObject:', parentField);
              return of(null);
            }
          })
        );
    } else {
      currentDefinition$ = of(this.initialObjectDefinition);
    }

    return currentDefinition$
      .pipe(
        tap(definition => {
          if (!definition) {
            console.warn('[FieldPathInput] No definition available for suggestions');
            this.isLoadingSuggestionsSubject.next(false);
          } else {
            console.log(`[FieldPathInput] Processing definition with ${definition.fields?.length || 0} fields`);
          }
        }),
        map(definition => {
          if (!definition || !definition.fields) {
            return [];
          }
          const segmentLower = currentInputSegment.toLowerCase();
          const filteredFields = definition.fields
            .filter(field => {
              const labelMatch = field.label.toLowerCase().includes(segmentLower);
              const nameMatch = field.name.toLowerCase().includes(segmentLower);
              return labelMatch || nameMatch;
            });

          const suggestions = filteredFields.map(field => ({
            path: parentPath ? `${parentPath}.${field.name}` : field.name,
            label: field.label,
            field: field,
          }));

          // Sort suggestions alphabetically by label
          suggestions.sort((a, b) => a.label.localeCompare(b.label));

          return suggestions;
        }),
        catchError(err => {
          console.error('[FieldPathInput] Error during suggestion filtering/mapping:', err);
          return of([]);
        }),
        finalize(() => {
          this.isLoadingSuggestionsSubject.next(false);
        })
      );
  }

  private loadSuggestionsForCurrentPath(openAfterLoad: boolean = true): void {
    if (!this.canEnterMoreSegments) {
      return;
    }

    const currentInputValue = this.currentInput.value ?? '';

    this.isLoadingSuggestionsSubject.next(true);
    this.cdRef.markForCheck();

    defer(() => this.fetchSuggestions(currentInputValue))
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe({
        next: (suggestions) => {
          this.suggestions$ = of(suggestions);
          this.isLoadingSuggestionsSubject.next(false);

          if (openAfterLoad) {
            this.openDropdown();
            this.cdRef.detectChanges();
          }
        },
        error: (err) => {
          console.error('[FieldPathInput] Error during loadSuggestionsForCurrentPath:', err);
          this.isLoadingSuggestionsSubject.next(false);
        },
      });
  }

  // --- Path Resolution & Formatting Logic (Moved/Adapted from Dialog) ---
  private getCachedDefinition(objectName: string): Observable<ObjectDefinitionModel | null> {
    if (!this.definitionCache.has(objectName)) {
      const definition$ = this.objectRestServiceCreator.create(objectName).getDefinition()
        .pipe(
          catchError(err => {
            console.error(`[FieldPathInput] Error fetching definition for ${objectName}:`, err);
            return of(null); // Return null on error
          }),
          shareReplay(1)
        );
      this.definitionCache.set(objectName, definition$);
    }
    return this.definitionCache.get(objectName)!;
  }

  private resolveFieldPath(path: string, startDefinition: ObjectDefinitionModel): Observable<CustomFieldModel | null> {
    const segments = path.split('.');
    const currentDefinition$: Observable<ObjectDefinitionModel | null> = of(startDefinition);

    // Pipe through segments using reduce to chain observables
    return segments.reduce((accDef$, segment) => {
      return accDef$
        .pipe(
          switchMap(def => {
            if (!def || !def.fields) {
              return of(null);
            } // No definition to search

            let field = def.fields.find(f => f.name === segment);
            if (!field) {
              const segmentLower = segment.toLowerCase();
              field = def.fields.find(f => f.label.toLowerCase() === segmentLower);
            }
            if (!field) {
              return of(null);
            } // Field not found in current def

            // Check if this is the last segment - if so, we are done resolving definition
            if (segment === segments[segments.length - 1]) {
              // This switchMap is for the *parent* definition, return it so the next step gets the correct field
              return of(def);
            }

            // Not the last segment, check if it's a lookup to get the *next* definition
            if (field.type === CustomFieldTypesEnum.LOOKUP && field.lookupObject?.name) {
              return this.getCachedDefinition(field.lookupObject.name);
            } else {
              return of(null); // Path broken (segment not lookup)
            }
          })
        );
    }, currentDefinition$)
      .pipe(
        // After reducing/resolving the definition containing the final segment,
        // find the actual final field within that definition.
        map(finalDef => {
          if (!finalDef) {
            return null;
          }
          const lastSegment = segments[segments.length - 1];
          let finalField = finalDef.fields.find(f => f.name === lastSegment);
          if (!finalField) {
            const segmentLower = lastSegment.toLowerCase();
            finalField = finalDef.fields.find(f => f.label.toLowerCase() === segmentLower);
          }
          return finalField ?? null;
        }),
        catchError(err => {
          console.error(`[FieldPathInput] Error resolving path '${path}':`, err);
          return of(null);
        }),
        takeUntil(this.destroy$)
      );
  }

  private checkPathEndType(path: string | null): void {
    if (!path) {
      this.canEnterMoreSegments = !this.currentInput.disabled;
      this.cdRef.markForCheck();
      return;
    }

    this.fieldPathResolverService.resolveFieldPath(path, this.initialObjectDefinition)
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe({
        next: (field) => {
          const isLookup = field && field.type === CustomFieldTypesEnum.LOOKUP;
          this.canEnterMoreSegments = isLookup && !this.currentInput.disabled;
          this.isFinalEditable = this.fullPath.includes('.') && !isLookup;
          this.showPlaceholder = false;
          this.cdRef.markForCheck();
        },
        error: (err) => {
          this.canEnterMoreSegments = false;
          this.cdRef.markForCheck();
        },
      });
  }

  private formatPathLabel(path: string): string {
    return path.split('.').map(s => s.charAt(0).toUpperCase() + s.slice(1)).join(' / ');
  }

  private buildPathLabel(parentPath: string, field: CustomFieldModel): string {
    if (!parentPath) {
      // No parent path, just return the field label
      return field.label;
    }

    // For paths with parent segments, resolve labels step by step
    const segments = parentPath.split('.');
    const pathLabels: string[] = [];

    // Start with the initial definition
    const currentDefinition = this.initialObjectDefinition;

    for (let i = 0; i < segments.length; i++) {
      const segment = segments[i];

      if (!currentDefinition?.fields) {
        // No fields available, fallback to segment name
        pathLabels.push(segment.charAt(0).toUpperCase() + segment.slice(1));
        continue;
      }

      const segmentField = currentDefinition.fields.find(f => f.name === segment);
      if (!segmentField) {
        // Field not found, fallback to segment name
        pathLabels.push(segment.charAt(0).toUpperCase() + segment.slice(1));
        continue;
      }

      // Use the field's translated label
      pathLabels.push(segmentField.label);

      // If this is a lookup field and not the last segment, try to get the lookup definition
      if (i < segments.length - 1 && segmentField.type === CustomFieldTypesEnum.LOOKUP && segmentField.lookupObject?.name) {
        const objectName = segmentField.lookupObject.name;
        const cachedDefinition$ = this.definitionCache.get(objectName);

        if (cachedDefinition$) {
          // We have a cached observable, but since this is synchronous, we can't wait for it
          // We'll just use what we have so far and let the async resolution handle the full path
          break;
        } else {
          // No cached definition available
          break;
        }
      }
    }

    // Add the current field label
    pathLabels.push(field.label);

    return pathLabels.join(' / ');
  }

  private preloadDefinitionsForPath(path: string): void {
    if (!path) {
      return;
    }

    const segments = path.split('.');
    let currentDefinition: ObjectDefinitionModel | null = this.initialObjectDefinition;

    for (let i = 0; i < segments.length - 1; i++) {
      const segment = segments[i];

      if (!currentDefinition?.fields) {
        break;
      }

      const field = currentDefinition.fields.find(f => f.name === segment);
      if (!field || field.type !== CustomFieldTypesEnum.LOOKUP || !field.lookupObject?.name) {
        break;
      }

      const objectName = field.lookupObject.name;

      // Pre-load the definition
      this.getCachedDefinition(objectName).pipe(
        take(1),
        takeUntil(this.destroy$)
      ).subscribe({
        next: (def) => {
          currentDefinition = def;
          // After loading each definition, refresh segment labels
          this.segmentLabels = this.resolveSegmentLabels();
          this.cdRef.markForCheck();
        },
        error: (err) => {
          console.error(`[FieldPathInput] Error pre-loading definition for ${objectName}:`, err);
        },
      });
    }
  }

  // ControlValueAccessor methods
  onChange: (value: string | null) => void = () => {
  };
  onTouched: () => void = () => {
  };

  // --- ControlValueAccessor Implementation ---
  writeValue(value: string | null): void {
    this.fullPath = value;
    this.parsePath();
    this.checkPathEndType(this.fullPath);
    this.currentInput.setValue('', {emitEvent: false});

    // Pre-load definitions for better label resolution
    if (value) {
      this.preloadDefinitionsForPath(value);
    }

    this.cdRef.markForCheck();
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    isDisabled ? this.currentInput.disable() : this.currentInput.enable();
    if (isDisabled) {
      this.canEnterMoreSegments = false;
    } else {
      this.checkPathEndType(this.fullPath);
    }
    this.cdRef.markForCheck();
  }
}
